import { ValidationError } from '../../shared/errors/BaseErrors';

export type CrawlerStatus = 'new' | 'code_sent' | 'active' | 'paused' | 'releasing' | 'released' | 'importing' | 'import_failed';

export type CrawlerSource = 'manual' | 'imported';

export interface CrawlerEntity {
  uuid: string;
  phone: string;
  status: CrawlerStatus;
  connectedAt?: string;
  telegramKnownSince?: string;
  cooldownUntil?: string;
  pausedReason: string;
  ageDays: number;
  releaseNeedsWarning: boolean;
  createdAt: string;
  source: CrawlerSource;
  importError: string;
  importAttempts: number;
  listeningChannels: number;
  pendingChannels: number;
}

export interface CrawlerImportPayload {
  profileFile: File;
  sessionFile: File;
}

export const normalizeCrawlerImportPayload = (payload: Partial<CrawlerImportPayload>): CrawlerImportPayload => {
  if (!payload.profileFile) throw new ValidationError('crawler-import-profile-required');
  if (!payload.sessionFile) throw new ValidationError('crawler-import-session-required');
  return { profileFile: payload.profileFile, sessionFile: payload.sessionFile };
};

const PHONE_SEPARATORS = /[\s\-()+]/g;
const PHONE_DIGITS = /^\d{7,15}$/;
const PHONE_ALLOWED = /^[\d\s\-()+]+$/;

export const normalizeCrawlerPhone = (raw: string): string => {
  const trimmed = (raw ?? '').trim();
  if (!PHONE_ALLOWED.test(trimmed)) throw new ValidationError('crawler-phone-invalid');
  const digits = trimmed.replace(PHONE_SEPARATORS, '');
  if (!PHONE_DIGITS.test(digits)) throw new ValidationError('crawler-phone-invalid');
  return `+${digits}`;
};

export const normalizeCrawlerCode = (raw: string): string => {
  const code = (raw ?? '').trim();
  if (!code) throw new ValidationError('crawler-code-required');
  return code;
};

const CODE_REQUESTABLE: CrawlerStatus[] = ['new', 'code_sent', 'paused'];

export const canRequestCrawlerCode = (crawler: CrawlerEntity): boolean => CODE_REQUESTABLE.includes(crawler.status);

export const canConfirmCrawlerCode = (crawler: CrawlerEntity): boolean => crawler.status === 'code_sent';

export const canReleaseCrawler = (crawler: CrawlerEntity): boolean => crawler.status === 'active';

export const isCrawlerImporting = (crawler: CrawlerEntity): boolean => crawler.status === 'importing';

export const isCrawlerImportFailed = (crawler: CrawlerEntity): boolean => crawler.status === 'import_failed';

export const isCrawlerInCooldown = (crawler: CrawlerEntity, now: Date = new Date()): boolean =>
  !!crawler.cooldownUntil && new Date(crawler.cooldownUntil).getTime() > now.getTime();
